
import React, { useEffect, useState } from "react";
import {
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Container,
  Grid,
  Autocomplete,
  createFilterOptions,
  Snackbar,
  Alert,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { fetchThingDetails } from "../../../../features/thing/thingThunk";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { setIsVehicle } from "../../../../features/vehicle/vehicleSlice";
import axios from "axios";

const rfidOptions = [
  { label: "Indian Reader", value: "INDIAN" },
  { label: "Chinese Reader 1", value: "CHINESE" },
  { label: "Chinese Reader 2", value: "CHINESE2" },
   { label: "MIFARE Reader", value: "MIFARE" },
];

const typeOptions = ["SHARED_VEHICLE", "PRIVATE_VEHICLE", "Other"];

// Search only on thingId + nickName
const vehicleFilter = createFilterOptions({
  stringify: (option) =>
    typeof option === "string"
      ? option
      : `${option.thingId || ""} ${option.nickName || ""}`,
});

const EditDetailsForm = () => {
  const dispatch = useDispatch();

  let isVehicle = useSelector((state) => state.vehicle.isVehicle) || "";
  const { things, loading } = useSelector((state) => state.things);

  const selectedThingData = isVehicle ? things.find((e) => e.id === isVehicle) : null;
  const isDevicesReadOnly = selectedThingData?.tempThing || selectedThingData?.tempThingFor;

  const initialFormState = {
    vehicleNumber: "",
    name: "",
    nickname: "",
    simNumber: "",
    rechargeExpiry: "",
    certificateExpiry: "",
    description: "",
    type: "SHARED_VEHICLE",
    speedLimit: "",
    deviceManufacturer: "",
    deviceModelNumber: "",
    devices: "",
    externalHandle: "",
    journeysStatus: "enable",
    accessAlert: "enable",
    indianRfidReader: "",
    tags: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Modal State
  const [openRemoveModal, setOpenRemoveModal] = useState(false);

  const [mfgModelList, setMfgModelList] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const resetForm = () => {
    setFormData(initialFormState);
    dispatch(setIsVehicle(""));
  };

  useEffect(() => {
    const loadMfgAndModels = async () => {
      try {
        const sessionToken = localStorage.getItem("sessionToken");
        const response = await axios.get("https://v3.dhundhoo.com/device/mfgAndModel", {
          headers: { "x-dhundhoo-session": sessionToken },
        });

        if (response.data) {
          setMfgModelList(response.data);
          const uniqueMfgs = [...new Set(response.data.map((item) => item.manufacturer))];
          setManufacturers(uniqueMfgs);
        }
      } catch (err) {
        console.error("Error fetching Manufacturers/Models:", err);
      }
    };

    loadMfgAndModels();
    dispatch(fetchThingDetails());
  }, [dispatch]);

  // Populate the model dropdown based on the selected manufacturer
  useEffect(() => {
    if (formData.deviceManufacturer) {
      const models = mfgModelList
        .filter((item) => item.manufacturer === formData.deviceManufacturer)
        .map((item) => item.model ?? item.modelNumber) // adjust to your API's field name
        .filter(Boolean);
      setFilteredModels([...new Set(models)]);
    } else {
      setFilteredModels([]);
    }
  }, [formData.deviceManufacturer, mfgModelList]);

  useEffect(() => {
    if (things && isVehicle) {
      const selectedThing = things.find((e) => e.id === isVehicle);
      if (selectedThing) {
        setFormData({
          vehicleNumber: selectedThing.thingId || "",
          name: selectedThing.name || "",
          nickname: selectedThing.nickName || "",
          simNumber: selectedThing.simNumber || "",
          rechargeExpiry: selectedThing.rechargeExpiryDate ? new Date(selectedThing.rechargeExpiryDate).toISOString().split('T')[0] : "",
          certificateExpiry: selectedThing.documentExpiryDate ? new Date(selectedThing.documentExpiryDate).toISOString().split('T')[0] : "",
          description: selectedThing.description || "",
          type: selectedThing.type || "SHARED_VEHICLE",
          devices: Array.isArray(selectedThing.devices) ? selectedThing.devices.join(", ") : selectedThing.devices || "",
          deviceManufacturer: selectedThing.deviceManufacturer || "",
          deviceModelNumber: selectedThing.deviceModelNumber || "",
          externalHandle: selectedThing.externalHandle || "",
          speedLimit: selectedThing.speedLimit || "",
          journeysStatus: selectedThing.disableJourneys ? "disable" : "enable",
          accessAlert: selectedThing.disableAccessAlerts ? "disable" : "enable",
          tags: Array.isArray(selectedThing.tags) ? selectedThing.tags.join(", ") : "",
          indianRfidReader: selectedThing.rfidType !== undefined && selectedThing.rfidType !== null
            ? selectedThing.rfidType
            : "",
        });
      }
    }
  }, [things, isVehicle]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sessionToken = localStorage.getItem("token");

    const apiEndpoint = isVehicle
      ? "https://v3.dhundhoo.com/thing/edit"
      : "https://v3.dhundhoo.com/thing/add";

    let deviceList = "";
    const isDevicesEmpty = !formData.devices || formData.devices.trim() === "";

    if (!isDevicesEmpty) {
      deviceList = formData.devices.split(",").map(d => d.trim()).filter(d => d !== "");
    } else {
      deviceList = [""];
    }

    const tagList = typeof formData.tags === "string"
      ? formData.tags.split(",").map(t => t.trim()).filter(t => t !== "")
      : [];

    const payload = {
      thingId: formData.vehicleNumber,
      name: formData.name,
      nickName: formData.nickname,
      deviceManufacturer: isDevicesEmpty ? "" : (formData.deviceManufacturer || ""),
      deviceModelNumber: isDevicesEmpty ? "" : (formData.deviceModelNumber || ""),
      rfidType: formData.indianRfidReader === "" ? null : formData.indianRfidReader,
      simNumber: formData.simNumber || null,
      rechargeExpiryDate: formData.rechargeExpiry ? new Date(formData.rechargeExpiry).getTime() : null,
      documentExpiryDate: formData.certificateExpiry ? new Date(formData.certificateExpiry).getTime() : null,
      description: formData.description || "",
      type: formData.type || "SHARED_VEHICLE",
      devices: deviceList,
      externalHandle: formData.externalHandle || "",
      disableJourneys: formData.journeysStatus === "disable",
      disableAccessAlerts: formData.accessAlert === "disable",
      speedLimit: formData.speedLimit ? parseInt(formData.speedLimit) : null,
      tags: tagList,
    };

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": sessionToken,
        },
        body: JSON.stringify(isVehicle ? payload : { things: [payload] }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: isVehicle ? "Vehicle updated successfully!" : "Vehicle added successfully!",
          severity: "success",
        });

        if (!isVehicle) {
          resetForm();
        }
        dispatch(fetchThingDetails());
      } else {
        const result = await response.json();
        setSnackbar({
          open: true,
          message: "Error: " + (result.message || "Failed to process"),
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Network Error: Check connection.",
        severity: "error",
      });
    }
  };

  // ----- REMOVE VEHICLE API LOGIC -----
  const handleRemoveConfirm = async () => {
    const sessionToken = localStorage.getItem("token");
    const removeApiEndpoint = `https://v3.dhundhoo.com/thing/remove/${formData.vehicleNumber}`;

    try {
      const response = await fetch(removeApiEndpoint, {
        method: "GET",
        headers: {
          "x-dhundhoo-session": sessionToken,
        },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Vehicle removed successfully!",
          severity: "success",
        });
        setOpenRemoveModal(false);
        resetForm();
        dispatch(fetchThingDetails());
      } else {
        setSnackbar({
          open: true,
          message: "Error: Failed to remove vehicle",
          severity: "error",
        });
        setOpenRemoveModal(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Network Error: Check connection.",
        severity: "error",
      });
      setOpenRemoveModal(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  const radioStyles = {
    color: "#26c6da",
    "&.Mui-checked": { color: "#26c6da" },
    padding: "4px 9px",
  };

  const labelProps = {
    xs: 12,
    sm: 3,
    sx: { textAlign: { xs: "left", sm: "right" }, pr: { sm: 2 }, alignSelf: "center" }
  };
  const labelTextStyles = { fontSize: "14px", color: "#333" };

  return (
    <Container maxWidth={false} sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <MDBox sx={{ width: "100%", maxWidth: "900px", padding: 4 }}>
        <form onSubmit={handleSubmit}>

          {/* Vehicle Number (search on thingId + nickName) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>* Vehicle number :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Autocomplete
                freeSolo
                options={things || []}
                filterOptions={vehicleFilter}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : `${option.thingId}`
                }
                value={things?.find(t => t.id === isVehicle) || formData.vehicleNumber}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, vehicleNumber: newInputValue });
                }}
                onChange={(event, newValue) => {
                  if (!newValue) {
                    resetForm();
                    return;
                  }
                  if (typeof newValue === "object") {
                    dispatch(setIsVehicle(newValue.id));
                  } else {
                    setFormData({ ...formData, vehicleNumber: newValue });
                    dispatch(setIsVehicle(""));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search or add vehicle number" fullWidth required size="small" />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{option.nickName}</span>
                    <span style={{ color: 'gray', fontSize: '0.85rem' }}>{option.thingId}</span>
                  </li>
                )}
              />
            </Grid>
          </Grid>

          {/* Name */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>* Name :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="name" value={formData.name} onChange={handleChange} fullWidth required size="small" />
            </Grid>
          </Grid>

          {/* Nickname */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>* Nickname :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="nickname" value={formData.nickname} onChange={handleChange} fullWidth required size="small" />
            </Grid>
          </Grid>

          {/* Sim Number */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Sim Number :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="simNumber" placeholder="Enter Sim Number" value={formData.simNumber} onChange={handleChange} fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Recharge Expiry */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Recharge Expiry :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="rechargeExpiry" type="date" value={formData.rechargeExpiry || ""} onChange={handleChange} fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Certificate Expiry */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Certificate Expiry :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="certificateExpiry" type="date" value={formData.certificateExpiry || ""} onChange={handleChange} fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Speed Limit */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Speed Limit (kmph) :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="speedLimit" value={formData.speedLimit} onChange={handleChange} fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Description */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Description :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="description" value={formData.description} onChange={handleChange} fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Type */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>* Type :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Autocomplete
                options={typeOptions}
                value={formData.type}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, type: newValue || "" });
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "38px",
                  },
                }}
                renderInput={(params) => <TextField {...params} required size="small" />}
              />
            </Grid>
          </Grid>

          {/* Tags */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Tags :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField name="tags" value={formData.tags} onChange={handleChange} placeholder="+ New Tag" fullWidth size="small" />
            </Grid>
          </Grid>

          {/* Editing Mode Conditional Fields */}
          {isVehicle && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item {...labelProps}>
                  <Typography sx={labelTextStyles}>Devices :</Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <TextField
                    name="devices"
                    value={formData.devices || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: isDevicesReadOnly }}
                    disabled={isDevicesReadOnly}
                  />
                </Grid>
              </Grid>

              {formData.devices && formData.devices.trim() !== "" && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item {...labelProps}>
                      <Typography sx={labelTextStyles}>Device Manufacturer :</Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Autocomplete
                        options={manufacturers}
                        value={formData.deviceManufacturer}
                        onChange={(event, newValue) => setFormData({ ...formData, deviceManufacturer: newValue || "", deviceModelNumber: "" })}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "38px",
                          },
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item {...labelProps}>
                      <Typography sx={labelTextStyles}>Device Model Number :</Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Autocomplete
                        options={filteredModels}
                        value={formData.deviceModelNumber}
                        disabled={!formData.deviceManufacturer}
                        onChange={(event, newValue) => setFormData({ ...formData, deviceModelNumber: newValue || "" })}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "38px",
                          },
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item {...labelProps}>
                      <Typography sx={labelTextStyles}>RFID Reader Type :</Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Autocomplete
                        options={rfidOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={rfidOptions.find((opt) => opt.value === formData.indianRfidReader) || rfidOptions[0]}
                        onChange={(event, newValue) => {
                          setFormData({ ...formData, indianRfidReader: newValue ? newValue.value : null });
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "38px",
                          },
                        }}
                        renderInput={(params) => <TextField {...params} size="small" required />}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>External Handle :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField
                name="externalHandle"
                value={formData.externalHandle || ""}
                fullWidth
                size="small"
                disabled
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          {/* Journeys Status */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Journeys Status :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <RadioGroup row name="journeysStatus" value={formData.journeysStatus} onChange={handleChange}>
                <FormControlLabel
                  value="enable"
                  control={<Radio sx={radioStyles} />}
                  label={<Typography sx={{ fontSize: "14px", color: "#555" }}>Enable Journeys</Typography>}
                />
                <FormControlLabel
                  value="disable"
                  control={<Radio sx={radioStyles} />}
                  label={<Typography sx={{ fontSize: "14px", color: "#555" }}>Disable Journeys</Typography>}
                  sx={{ ml: 2 }}
                />
              </RadioGroup>
            </Grid>
          </Grid>

          {/* Access (RFID) Alert */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item {...labelProps}>
              <Typography sx={labelTextStyles}>Access (RFID) Alert :</Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <RadioGroup row name="accessAlert" value={formData.accessAlert} onChange={handleChange}>
                <FormControlLabel
                  value="enable"
                  control={<Radio sx={radioStyles} />}
                  label={<Typography sx={{ fontSize: "14px", color: "#555" }}>Enable Access Alerts</Typography>}
                />
                <FormControlLabel
                  value="disable"
                  control={<Radio sx={radioStyles} />}
                  label={<Typography sx={{ fontSize: "14px", color: "#555" }}>Disable Access Alerts</Typography>}
                  sx={{ ml: 2 }}
                />
              </RadioGroup>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid container spacing={2}>
            <Grid item {...labelProps}></Grid>
            <Grid item xs={12} sm={9} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <MDButton
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#FB8C00",
                  color: '#fff !important',
                  textTransform: "none",
                  fontWeight: "normal",
                  "&:hover": { backgroundColor: "#f3b467" }
                }}
              >
                {isVehicle ? "Update thing" : "Add thing"}
              </MDButton>
              <MDButton
                variant="outlined"
                onClick={resetForm}
                sx={{
                  borderColor: "#ccc",
                  color: "#555 !important",
                  textTransform: "none",
                  fontWeight: "normal"
                }}
              >
                Clear
              </MDButton>
              {isVehicle && (
                <MDButton
                  variant="text"
                  onClick={() => setOpenRemoveModal(true)}
                  sx={{
                    color: "#d32f2f !important",
                    textTransform: "none",
                    fontWeight: "normal",
                    ml: "auto"
                  }}
                >
                  Remove
                </MDButton>
              )}
            </Grid>
          </Grid>

        </form>
      </MDBox>

      {/* Confirmation Modal for Removal */}
      <Dialog
        open={openRemoveModal}
        onClose={() => setOpenRemoveModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Vehicle Removal"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to remove this vehicle ({formData.vehicleNumber})? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRemoveModal(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirm} color="error" autoFocus>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default EditDetailsForm;
